import { useState } from "react";
import {
  Box,
  Button,
  Collapse,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import { RequestFile, SignRequest } from "models/interfaces";
import { downloadSignedDocument } from "../MySignRequestHelper";
import viewSignRequestStyles from "./ViewSignRequest.styles";
import viewSignRequestTokens from "./ViewSignRequest.tokens";
import {
  ChevronDownGlyph,
  DocumentGlyph,
  DownloadGlyph,
  EyeGlyph,
} from "./ViewRequestIcons";
import EmptyState from "global/components/EmptyState/EmptyState";
import { tourAttr } from "tour";

const styles = viewSignRequestStyles;
const { documentRow } = viewSignRequestTokens;

/** Shown instead of a file name when the card is listing the single merged PDF. */
const MERGED_DOC_LABEL = "Signed document";
/** Says why there is one row rather than one per uploaded file. */
const MERGED_DOC_NOTE =
  "Your account merges signed documents into a single PDF.";

interface DocumentsCardProps {
  signRequest: SignRequest;
  /** Opens the document viewer modal at the chosen document index. */
  onPreview: (index: number) => void;
  /** Downloading is only offered once the signed bundle exists. */
  canDownload: boolean;
  /**
   * True once the request is signed AND the account merges every signed file
   * into one certificated PDF. The backend then serves that same merged
   * document for Preview and Download of every file, so one row per upload
   * meant N rows that all opened identical content.
   */
  mergedOnly?: boolean;
  /** Signed requests open the review screen rather than the plain preview, so the action says so. */
  withSummary?: boolean;
}

/** "PDF · 8 pages" — the design's per-file meta line. */
const getDocumentMeta = (file: RequestFile) => {
  const extension = (file.unsignedPdf || file.unsignedDoc || "")
    .split(".")
    .pop();
  const parts = [extension ? extension.toUpperCase() : "PDF"];
  if (file.pageCount) {
    parts.push(`${file.pageCount} page${file.pageCount > 1 ? "s" : ""}`);
  }
  return parts.join(" · ");
};

/**
 * Documents card: one row per file with its thumbnail, name/meta and the
 * Preview + Download actions, collapsible from the header chevron.
 */
const DocumentsCard: React.FC<DocumentsCardProps> = ({
  signRequest,
  onPreview,
  canDownload,
  mergedOnly = false,
  withSummary = false,
}) => {
  const [expanded, setExpanded] = useState(true);
  const [downloadingIndex, setDownloadingIndex] = useState<number | null>(null);
  const files = signRequest.files ?? [];
  // One row for the merged PDF; every other mode keeps a row per uploaded file.
  const listFiles = mergedOnly ? files.slice(0, 1) : files;

  const handleDownload = async (file: RequestFile, index: number) => {
    setDownloadingIndex(index);
    await downloadSignedDocument(signRequest, file, (loading: boolean) =>
      setDownloadingIndex(loading ? index : null),
    );
  };

  return (
    <Box {...tourAttr("viewRequest.documents")} sx={styles.card}>
      {/* Below `md` the rows carry the rule on their own top edge. */}
      <Box sx={styles.sectionHeader(false)}>
        <Box sx={styles.sectionHeaderLead}>
          <Box component="h2" sx={styles.sectionTitle}>
            Documents
          </Box>
          <Box component="span" sx={styles.sectionCount()}>
            {listFiles.length}
          </Box>
        </Box>
        <IconButton
          disableRipple
          onClick={() => setExpanded((value) => !value)}
          aria-label={expanded ? "Collapse documents" : "Expand documents"}
          sx={styles.sectionToggle(expanded)}
        >
          <ChevronDownGlyph sx={styles.sectionToggleGlyph} />
        </IconButton>
      </Box>

      <Collapse in={expanded} timeout="auto" unmountOnExit>
        {listFiles.length === 0 ? (
          <EmptyState
            size="medium"
            icon="doc"
            title="No documents on this request"
          />
        ) : (
          listFiles.map((file, index) => {
            // The merged PDF is not any one uploaded file, so naming the single
            // row after the first upload would be wrong.
            const name = mergedOnly
              ? MERGED_DOC_LABEL
              : file.unsignedPdf || file.unsignedDoc || "Document";
            return (
              <Box
                key={`${name}-${index}`}
                sx={styles.documentRow}
                onClick={() => onPreview(index)}
              >
                <Box sx={styles.documentThumb}>
                  <DocumentGlyph sx={styles.documentThumbGlyph} />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Tooltip title={name} arrow describeChild>
                    <Typography component="div" sx={styles.documentName}>
                      {name}
                    </Typography>
                  </Tooltip>
                  <Typography component="div" sx={styles.documentMeta}>
                    {/* The merged PDF spans every file, so the first file’s page
                        count would understate it. Show the type alone. */}
                    {mergedOnly ? "PDF" : getDocumentMeta(file)}
                  </Typography>
                </Box>
                <Button
                  disableRipple
                  onClick={(event) => {
                    event.stopPropagation();
                    onPreview(index);
                  }}
                  sx={styles.documentAction}
                >
                  <EyeGlyph
                    sx={{
                      width: documentRow.actionIconSize,
                      height: documentRow.actionIconSize,
                    }}
                  />
                  {withSummary ? "Preview & Summary" : "Preview"}
                </Button>
                {canDownload && (
                  <IconButton
                    disableRipple
                    disabled={downloadingIndex === index}
                    onClick={(event) => {
                      event.stopPropagation();
                      handleDownload(file, index);
                    }}
                    aria-label={`Download ${name}`}
                    sx={styles.documentIconAction}
                  >
                    <DownloadGlyph sx={styles.documentIconGlyph} />
                  </IconButton>
                )}
              </Box>
            );
          })
        )}
        {mergedOnly && listFiles.length > 0 && (
          <Typography component="p" sx={styles.documentMergedNote}>
            {MERGED_DOC_NOTE}
          </Typography>
        )}
      </Collapse>
    </Box>
  );
};

export default DocumentsCard;
